/**
 * Camada de Mapa de Calor (Heatmap) Contínuo.
 *
 * Configurada para que concentrações de demandas (como múltiplos pins no mesmo local ou via)
 * expandam sua área de calor e atinjam o vermelho intenso cobrindo toda a extensão da demanda.
 *
 * @module components/HeatmapLayer
 */

import { useEffect, useMemo, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";

function logBestEffortCleanupError(error) {
  if (import.meta.env.DEV) {
    console.debug("[HeatmapLayer] Limpeza Leaflet ignorada:", error);
  }
}

/**
 * Componente principal da camada de calor.
 *
 * @param {{ points: Array<{ latitude: number, longitude: number }> }} props
 * @returns {null}
 */
export default function HeatmapLayer({ points = [] }) {
  const map = useMap();
  const heatLayerRef = useRef(null);

  // Limpa imediatamente quaisquer pernas de spiderfy ou resquícios do MarkerCluster no mapa
  useEffect(() => {
    if (!map) return;

    map.eachLayer((layer) => {
      // Remove pernas de spiderfy vinculadas a marcadores
      if (layer._spiderLeg) {
        try {
          map.removeLayer(layer._spiderLeg);
        } catch (e) {
          logBestEffortCleanupError(e);
        }
        delete layer._spiderLeg;
      }
      // Remove polylines que sejam hastes de spiderfy
      if (
        layer instanceof L.Polyline &&
        (layer.options?.className === "cluster-spider-leg" ||
          layer.options?.dashArray === "4, 4" ||
          layer._path?.classList?.contains("cluster-spider-leg") ||
          layer._path?.classList?.contains("leaflet-cluster-spider-leg"))
      ) {
        try {
          map.removeLayer(layer);
        } catch (e) {
          logBestEffortCleanupError(e);
        }
      }
    });

    // Remove botões de fechar aranha (✕) e classes residuais do container
    const container = map.getContainer();
    if (container) {
      container.querySelectorAll(".cluster-close-btn").forEach((el) => el.remove());
      container.querySelectorAll(".cluster-is-spiderfied").forEach((el) => {
        el.classList.remove("cluster-is-spiderfied");
      });
    }
  }, [map]);

  // Pontos válidos com coordenadas numéricas
  const validPoints = useMemo(() => {
    return (points || []).filter(
      (p) => p && typeof p.latitude === "number" && typeof p.longitude === "number"
    );
  }, [points]);

  // Calcula a densidade máxima do conjunto atual via agrupamento em grade espacial O(N)
  const dynamicMax = useMemo(() => {
    const len = validPoints.length;
    if (!validPoints || len === 0) return 3.0;
    if (len <= 10) return 2.0;

    // Tamanho da célula da grade espacial correspondente ao raio de vizinhança (~350 metros)
    const cellSize = 0.0035;
    const grid = new Map();

    // 1. Agrupamento em grade espacial em O(N)
    for (let i = 0; i < len; i++) {
      const p = validPoints[i];
      const cellX = Math.floor(p.latitude / cellSize);
      const cellY = Math.floor(p.longitude / cellSize);
      const key = `${cellX},${cellY}`;
      grid.set(key, (grid.get(key) || 0) + 1);
    }

    // 2. Apuração da densidade local por célula somada às 8 células vizinhas
    const cellDensities = [];
    let maxDensity = 1;

    for (const [key, count] of grid.entries()) {
      const commaIdx = key.indexOf(",");
      const cx = parseInt(key.substring(0, commaIdx), 10);
      const cy = parseInt(key.substring(commaIdx + 1), 10);

      let localDensity = count;
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          if (dx === 0 && dy === 0) continue;
          const neighborCount = grid.get(`${cx + dx},${cy + dy}`);
          if (neighborCount) {
            localDensity += neighborCount * 0.45;
          }
        }
      }

      cellDensities.push(localDensity);
      if (localDensity > maxDensity) maxDensity = localDensity;
    }

    // Percentil 95% para calibrar o topo da escala de cor
    cellDensities.sort((a, b) => a - b);
    const p95Index = Math.min(cellDensities.length - 1, Math.floor(cellDensities.length * 0.95));
    const p95Density = cellDensities[p95Index] || maxDensity;

    return Math.max(2.0, p95Density * 0.75);
  }, [validPoints]);

  // Opções de calor adaptativas ao nível de zoom
  const calculateHeatOptions = useMemo(() => {
    return (zoom) => {
      const zoomDiff = zoom - 13;
      // Raio e blur crescem com o zoom para preencher toda a área dos pins na aproximação
      const radius = Math.max(18, Math.min(48, Math.round(22 * Math.pow(1.14, Math.max(0, zoomDiff)))));
      const blur = Math.max(12, Math.min(30, Math.round(15 * Math.pow(1.12, Math.max(0, zoomDiff)))));

      return {
        radius,
        blur,
        maxZoom: 18,
        max: dynamicMax,
        minOpacity: 0.38,
        gradient: {
          0.15: "#2563eb",  // Azul (baixa concentração)
          0.32: "#06b6d4",  // Ciano
          0.50: "#10b981",  // Verde
          0.70: "#eab308",  // Amarelo
          0.85: "#f97316",  // Laranja
          1.00: "#ef4444",  // Vermelho vivo (alta concentração / eixos críticos)
        },
      };
    };
  }, [dynamicMax]);

  // Instanciação e atualização do Heatmap no mapa
  useEffect(() => {
    if (!map || typeof L.heatLayer !== "function" || validPoints.length === 0) return;

    const heatData = validPoints.map((p) => [p.latitude, p.longitude, 1.0]);

    const initialOptions = calculateHeatOptions(map.getZoom());
    const heatLayer = L.heatLayer(heatData, initialOptions).addTo(map);
    heatLayerRef.current = heatLayer;

    // Atualiza opções ao alterar o zoom para manter a escala e expansão das manchas
    const handleZoomEnd = () => {
      if (heatLayerRef.current) {
        const updatedOptions = calculateHeatOptions(map.getZoom());
        heatLayerRef.current.setOptions(updatedOptions);
      }
    };

    map.on("zoomend", handleZoomEnd);

    return () => {
      map.off("zoomend", handleZoomEnd);
      if (heatLayerRef.current) {
        try {
          map.removeLayer(heatLayerRef.current);
        } catch (e) {
          logBestEffortCleanupError(e);
        }
        heatLayerRef.current = null;
      }
    };
  }, [map, validPoints, calculateHeatOptions]);

  return null;
}



